import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import Login from "../pages/auth/Login"; // eager — entry point
import ProtectedRoute from "./ProtectedRoute";
import RoleProtectedRoute from "./RoleProtectedRoute";
import MobileBottomNav from "../components/MobileBottomNav";
import ContentProtection from "../components/ContentProtection";
import ChatWidget from "../components/ChatWidget";
import LoadingScreen from "../components/LoadingScreen";

// All pages lazy-loaded — only downloaded when the student/admin navigates there
const AdminDashboard     = lazy(() => import("../pages/admin/admindashboard"));
const Certificates       = lazy(() => import("../pages/admin/certificates"));
const CourseManagement   = lazy(() => import("../pages/admin/coursemanagement"));
const VideoManagement    = lazy(() => import("../pages/admin/videomanagement"));
const NotesManagement    = lazy(() => import("../pages/admin/notesmanagement"));
const StudentReport      = lazy(() => import("../pages/admin/studentreport"));
const QuizManagement     = lazy(() => import("../pages/admin/quizmanagement"));
const StudentManagement  = lazy(() => import("../pages/admin/studentmanagement"));
const AdminManagement    = lazy(() => import("../pages/admin/adminmanagement"));
const AdminMessages      = lazy(() => import("../pages/admin/drrmassages"));
const AdminAnnouncement  = lazy(() => import("../pages/admin/announcement"));
const AdminDoubts        = lazy(() => import("../pages/admin/doughts"));
const AdminTechnicalIssues = lazy(() => import("../pages/admin/technicalIssues"));
const VideoConcurrency   = lazy(() => import("../pages/admin/videoconcurrency"));
const ChatbotAccess      = lazy(() => import("../pages/admin/chatbotaccess"));
const WhatsappCenter     = lazy(() => import("../pages/admin/whatsappcenter"));
const LiveSessions       = lazy(() => import("../pages/admin/livesessions"));
const CourseSelector     = lazy(() => import("../pages/students/courseselector"));
const StudentDashboard   = lazy(() => import("../pages/students/studentdashboard"));
const CoursePlayer       = lazy(() => import("../pages/students/course"));
const CourseDetails      = lazy(() => import("../pages/students/coursedetail"));
const Certificate        = lazy(() => import("../pages/students/certificate"));
const ProgressReport     = lazy(() => import("../pages/students/progressreport"));
const StudentMessages    = lazy(() => import("../pages/students/studentmassages"));
const StudentAnnouncement = lazy(() => import("../pages/students/studentannounscement"));
const StudentDoubts      = lazy(() => import("../pages/students/studentdoughts"));
const StudentTechnicalIssues = lazy(() => import("../pages/students/technicalIssues"));
const StudentFaqsPage    = lazy(() => import("../pages/students/studentfaqs"));
const Profile            = lazy(() => import("../pages/students/profile"));
const MyCourse           = lazy(() => import("../pages/students/mycourse"));
const StudentQuiz        = lazy(() => import("../pages/students/studentquiz"));

function PageLoader() {
  return <LoadingScreen minHeight="60vh" />;
}

function Dashboard() {
  return <h1>Dashboard (Protected)</h1>;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <MobileBottomNav />
      <ContentProtection />
      <ChatWidget />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Login />} />

          {/* Normal Protected */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin" element={<RoleProtectedRoute role="admin"><AdminDashboard /></RoleProtectedRoute>} />
          <Route path="/admin/certificates" element={<RoleProtectedRoute role="admin"><Certificates /></RoleProtectedRoute>} />
          <Route path="/admin/course-management" element={<RoleProtectedRoute role="admin"><CourseManagement /></RoleProtectedRoute>} />
          <Route path="/admin/video-management" element={<RoleProtectedRoute role="admin"><VideoManagement /></RoleProtectedRoute>} />
          <Route path="/admin/notes-management" element={<RoleProtectedRoute role="admin"><NotesManagement /></RoleProtectedRoute>} />
          <Route path="/admin/progress-report" element={<RoleProtectedRoute role="admin"><StudentReport /></RoleProtectedRoute>} />
          <Route path="/admin/quiz-management" element={<RoleProtectedRoute role="admin"><QuizManagement /></RoleProtectedRoute>} />
          <Route path="/admin/student-management" element={<RoleProtectedRoute role="admin"><StudentManagement /></RoleProtectedRoute>} />
          <Route path="/admin/admin-management" element={<RoleProtectedRoute role="admin"><AdminManagement /></RoleProtectedRoute>} />
          <Route path="/admin/ram-messages" element={<RoleProtectedRoute role="admin"><AdminMessages /></RoleProtectedRoute>} />
          <Route path="/admin/announcements" element={<RoleProtectedRoute role="admin"><AdminAnnouncement /></RoleProtectedRoute>} />
          <Route path="/admin/doubts" element={<RoleProtectedRoute role="admin"><AdminDoubts /></RoleProtectedRoute>} />
          <Route path="/admin/technical-issues" element={<RoleProtectedRoute role="admin"><AdminTechnicalIssues /></RoleProtectedRoute>} />
          <Route path="/admin/video-concurrency" element={<RoleProtectedRoute role="admin"><VideoConcurrency /></RoleProtectedRoute>} />
          <Route path="/admin/chatbot-access" element={<RoleProtectedRoute role="admin"><ChatbotAccess /></RoleProtectedRoute>} />
          <Route path="/admin/whatsapp" element={<RoleProtectedRoute role="admin"><WhatsappCenter /></RoleProtectedRoute>} />
          <Route path="/admin/live-sessions" element={<RoleProtectedRoute role="admin"><LiveSessions /></RoleProtectedRoute>} />
          <Route path="/admin/profile" element={<RoleProtectedRoute role="admin"><Profile /></RoleProtectedRoute>} />

          {/* Student Routes */}
          <Route path="/student/select-course" element={<RoleProtectedRoute role="student"><CourseSelector /></RoleProtectedRoute>} />
          <Route path="/student" element={<RoleProtectedRoute role="student"><StudentDashboard /></RoleProtectedRoute>} />
          <Route path="/courses/:id" element={<RoleProtectedRoute role="student"><CourseDetails /></RoleProtectedRoute>} />
          <Route path="/course/:id" element={<RoleProtectedRoute role="student"><CoursePlayer /></RoleProtectedRoute>} />
          <Route path="/student/certificate" element={<RoleProtectedRoute role="student"><Certificate /></RoleProtectedRoute>} />
          <Route path="/student/progressreport" element={<RoleProtectedRoute role="student"><ProgressReport /></RoleProtectedRoute>} />
          <Route path="/student/mycourses" element={<RoleProtectedRoute role="student"><MyCourse /></RoleProtectedRoute>} />
          <Route path="/student/messages" element={<RoleProtectedRoute role="student"><StudentMessages /></RoleProtectedRoute>} />
          <Route path="/student/announcements" element={<RoleProtectedRoute role="student"><StudentAnnouncement /></RoleProtectedRoute>} />
          <Route path="/student/faqs" element={<RoleProtectedRoute role="student"><StudentFaqsPage /></RoleProtectedRoute>} />
          <Route path="/student/profile" element={<RoleProtectedRoute role="student"><Profile /></RoleProtectedRoute>} />
          <Route path="/student/doubts" element={<RoleProtectedRoute role="student"><StudentDoubts /></RoleProtectedRoute>} />
          <Route path="/student/technical-issues" element={<RoleProtectedRoute role="student"><StudentTechnicalIssues /></RoleProtectedRoute>} />
          <Route path="/student/quiz/:quizId" element={<StudentQuiz />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
